import { Fragment, ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import closeButton from '../../assets/icons/close.png'
import { useBottomModal } from '../../contexts/BottomModalContext'
import { useSpecies } from '../../contexts/SpeciesContext'
import { RemoteLoadExtract } from '../../data/use-case/RemoteLoadExtract'
import { RemoteLoadExtractDetails } from '../../data/use-case/RemoteLoadExtractDetails'
import { RemoteLoadUser } from '../../data/use-case/RemoteLoadUser'
import { RemoteLoadUserSpecies } from '../../data/use-case/RemoteLoadUserSpecies'
import { maskPoints } from '../../utils/mask'
import { Pagination } from '../Pagination'
import SelectFloatingLabel from '../SelectFloatingLabel'
import { DetailsData, PointsExtractModalProps } from './props'
import * as S from './style'

enum TableStates {
  LOADING,
  READY,
  EMPTY
}

enum ModalStates {
  LOADING,
  READY
}

const itemsPerPage = 8

const today = new Date()

const pastFromDate = (date: Date, months: number): Date => {
  const newDate = new Date(date.getTime())
  newDate.setMonth(newDate.getMonth() - months)
  return newDate
}

const loadExtract = new RemoteLoadExtract()
const loadUserSpecies = new RemoteLoadUserSpecies()
const loadUser = new RemoteLoadUser()
const loadExtractDetails = new RemoteLoadExtractDetails()

const accountOptions = [{
  optionTitle: 'Reinvestimento',
  optionValue: 'reinvestment'
},
{
  optionTitle: 'Benefício',
  optionValue: 'benefit'
}]

const periodOptions = [{
  optionTitle: 'Todo o período',
  optionValue: JSON.stringify({
    startDate: pastFromDate(today, 120).toISOString(),
    endDate: today.toISOString()
  })
}, {
  optionTitle: 'Últimos 12 meses',
  optionValue: JSON.stringify({
    startDate: pastFromDate(today, 12).toISOString(),
    endDate: today.toISOString()
  })
}, {
  optionTitle: 'Últimos 6 meses',
  optionValue: JSON.stringify({
    startDate: pastFromDate(today, 6).toISOString(),
    endDate: today.toISOString()
  })
}]

function PointsExtractModal ({ id }: PointsExtractModalProps): ReactElement {
  const { closeBottomModal } = useBottomModal()
  const { species } = useSpecies()
  const [data, setData] = useState<any[]>([])
  const [user, setUser] = useState<any>({})
  const [state, setState] = useState<TableStates>(TableStates.LOADING)
  const [modalState, setModalState] = useState<ModalStates>(ModalStates.LOADING)
  const [specieOptions, setSpecies] = useState<any[]>([])
  const [filter, setFilter] = useState({
    account: 'reinvestment',
    specie: 0,
    period: {
      startDate: pastFromDate(today, 12),
      endDate: today
    }
  })
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1
  })
  const [activeDetails, setActiveDetails] = useState(-1)
  const [details, setDetails] = useState<DetailsData[]>([])


  const min = useMemo(() => (pagination.page - 1) * itemsPerPage, [pagination.page])
  const max = useMemo(() => Math.min(min + itemsPerPage, data.length), [data, min])
  const pagedData = useMemo(() => {
    const page = [...data].splice(min, itemsPerPage)
    console.log(min, itemsPerPage, data)
    return page
  }, [data, min])

  const resetDetailsDisplay = useCallback(() => {
    setActiveDetails(-1)
  }, [])

  const resetPage = useCallback(() => {
    setPagination(pages => ({
      ...pages,
      page: 1,
    }))
  }, [])

  const handleChangeAccount = useCallback((account: string) => {
    const newFilter = { ...filter }
    newFilter.account = account
    if (account === 'benefit') {
      newFilter.specie = 81544
    } else if (newFilter.specie === 81544) {
      newFilter.specie = species[0].catalog
    }
    setFilter(newFilter)
  }, [filter, species])

  const handleUpdatePeriod = useCallback((period: any) => {
    const json = JSON.parse(period)
    const startDate = new Date(json.startDate)
    const endDate = new Date(json.endDate)
    setFilter(prev => ({ ...prev, period: { startDate, endDate } }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    resetDetailsDisplay()
    setPagination((prev) => ({ ...prev, page }))
  }, [resetDetailsDisplay])

  const handleActiveDetails = useCallback(async (index: number, parentOrderId: number) => {
    try {
      const response = await loadExtractDetails.handle({
        userId: id,
        parentOrderId,
        catalogId: filter.specie
      })
      setDetails(response.extract)
      setActiveDetails((prev) => prev === index ? -1 : index)
    } catch (error) {
    }
  }, [filter.specie, id])

  const formatDate = useCallback((date: string) => {
    return new Date(date).toLocaleDateString('pt-br')
  }, [])

  useEffect(() => {
  }, [id])

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      totalPages: Math.ceil(data.length / itemsPerPage)
    }))
  }, [data])

  useEffect(() => {
    if (!filter.specie) return
    void (async () => {
      try {
        setState(TableStates.LOADING)
        resetDetailsDisplay()
        resetPage()
        const response = await loadExtract.handle({
          userId: id,
          catalogId: filter.specie,
          startDate: filter.period.startDate,
          endDate: filter.period.endDate
        })
        setData(response.extract)
        setState(TableStates.READY)
        if (response.extract.length === 0) setState(TableStates.EMPTY)
      } catch (error) {
        setState(TableStates.EMPTY)
      }
    })()
  }, [id, filter, resetDetailsDisplay, resetPage])

  useEffect(() => {
    void (async () => {
      setModalState(ModalStates.LOADING)
      const response = await loadUserSpecies.handle({ userId: id })
      setSpecies(response.species.map((specie: any) => ({
        optionTitle: specie.name,
        optionValue: String(specie.catalogId)
      })))
      const user = await loadUser.handle({ userId: id })
      setUser(user)
      setFilter({
        account: 'reinvestment',
        specie: response.species[0].catalogId,
        period: {
          startDate: pastFromDate(today, 12),
          endDate: today
        }
      })
      setModalState(ModalStates.READY)
    })()
  }, [id, species])

  if (modalState === ModalStates.LOADING) return <>Carregando...</>

  return <S.Container>
    <S.CloseButton onClick={closeBottomModal}>
      <img src={closeButton} alt="Botão de fechar modal" />
    </S.CloseButton>
    <S.Header>
      <h1>Extrato de pontos de {user.name}</h1>
      <S.Filter>
        <SelectFloatingLabel
          selectTitle="Selecione uma conta"
          selectOptions={accountOptions}
          onValueChange={handleChangeAccount}
        />
        {filter.account === 'reinvestment' && <SelectFloatingLabel
          selectTitle="Selecione uma espécie"
          selectOptions={specieOptions}
          selectedValue={filter.specie}
          onValueChange={(specie) => setFilter((prev) => ({ ...prev, specie: Number.parseInt(specie, 10) }))}
        />}
        <SelectFloatingLabel
          selectTitle="Selecione um período"
          selectOptions={periodOptions}
          onValueChange={handleUpdatePeriod}
        />
      </S.Filter>
    </S.Header>
    {state === TableStates.EMPTY && <>Esse cliente não tem extrato para os critérios selecionados</>}
    {state === TableStates.READY && <><S.DesktopContainer>
      <S.Table>
        <thead>
          <tr>
            <td>Data</td>
            <td>Descrição</td>
            <td>Pontos</td>
            <td>Status</td>
          </tr>
        </thead>
        <tbody>
          {pagedData.map((item, index) => <Fragment key={index}>
            <tr>
              <td>{new Date(item.date).toLocaleDateString('pt-br')}</td>
              <td>
                <div>
                  <span>{item.description}</span>
                  {item.parentOrderId && <S.DetailsToggle onClick={() => handleActiveDetails(index, item.parentOrderId)} $open={index === activeDetails}>{activeDetails === index ? 'Ocultar detalhes' : 'Exibir detalhes'}</S.DetailsToggle>}
                </div>
              </td>
              <td>{maskPoints(item.valuePoints)}</td>
              <td>{item.type === 'CREDIT' ? 'Expira em' : 'Resgatado em'} {formatDate(item.date)}</td>
            </tr>
            {index === activeDetails && <S.NestedTableWrapper className={index === activeDetails ? 'active' : ''}>
              <td colSpan={4}>
                <table>
                  <thead>
                    <tr>
                      <th>Nome do produto</th>
                      <th>Quantidade</th>
                      <th>Parceiro</th>
                      <th>Pontos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((detail, detailIndex) => <tr key={detailIndex}>
                      <td>{detail.name}</td>
                      <td>{String(detail.amount).padStart(2, '0')}</td>
                      <td>{detail.partner}</td>
                      <td>{maskPoints(detail.points)}</td>
                    </tr>)}
                  </tbody>
                </table>
              </td>
            </S.NestedTableWrapper>}
          </Fragment>)}
        </tbody>
        <S.Caption>Resultado: {min} - {max} de {data.length}</S.Caption>
      </S.Table>
      <Pagination currentPage={pagination.page} onPageChange={handlePageChange} totalPages={pagination.totalPages} />
    </S.DesktopContainer>
      <S.MobileContainer>
        <S.MobileContent>
          <S.MobileItemsContainer>
            {pagedData.map((item, index) => <S.MobileItem>
              <p><strong>Data: </strong>{new Date(item.date).toLocaleDateString('pt-br')}</p>
              <p><strong>Descrição: </strong><br />{item.description}</p>
              <p><strong>Pontos: </strong>{maskPoints(item.valuePoints)}</p>
              <p><strong>Status: </strong>{item.type === 'CREDIT' ? 'Expira em' : 'Resgatado em'} {formatDate(item.date)}</p>
              {item.parentOrderId && <S.DetailsToggle onClick={() => handleActiveDetails(index, item.parentOrderId)} $open={index === activeDetails}>{activeDetails === index ? 'Ocultar detalhes' : 'Exibir detalhes'}</S.DetailsToggle>}
              {activeDetails === index && <S.MobileDetailsWrapper>
                <hr />
                {details.map((detail, detailIndex) => <Fragment key={index}>
                  <div key={detailIndex}>
                    <strong>{detail.name}</strong>
                    <ul>
                      <li>Quantidade: <strong>{detail.amount}</strong></li>
                      <li>Parceiro: <strong>{detail.partner}</strong></li>
                      <li>Valor: <strong>{maskPoints(detail.points)}</strong></li>
                    </ul>
                  </div>
                  <hr />
                </Fragment>)}
              </S.MobileDetailsWrapper>}
            </S.MobileItem>)}
          </S.MobileItemsContainer>
          <S.Caption>Resultado: {min} - {max} de {data.length}</S.Caption>
          <Pagination currentPage={pagination.page} onPageChange={handlePageChange} totalPages={pagination.totalPages} />
        </S.MobileContent>
      </S.MobileContainer></>}
  </S.Container>
}

export default PointsExtractModal
